import { useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { TABS } from './tabs';

/**
 * TabNav - 8-tab navigation for the AppShell (PROD-578).
 *
 * Tab IDs and canonical order live in `./tabs.ts`. Don't reorder.
 *
 * Implements:
 *   - WAI-ARIA tab nav semantics: role="tablist" + arrow / Home / End
 *     keyboard navigation per AS-6 (WCAG 2.1 AA).
 *   - Active state highlighted via Signal DS token-driven Tailwind classes.
 *
 * Telemetry is emitted by AppShell on route change (single source of truth);
 * the tab nav itself does not double-emit.
 */

/**
 * Match the same active-tab semantics NavLink uses internally, with the
 * PROD-581 forward-compat tightening:
 *   - exact pathname match, OR
 *   - pathname is a slash-delimited subpath of the tab path
 *
 * The bare `pathname.startsWith(tabPath)` form would silently false-match a
 * future sibling route like `/architecture-templates` against the
 * `/architecture` tab. Mirroring this rule here keeps `aria-selected` and
 * the `data-active-tab` resolution in `AppShell.pathToTabId` aligned.
 */
function isTabActive(pathname: string, tabPath: string): boolean {
  return pathname === tabPath || pathname.startsWith(tabPath + '/');
}

export function TabNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const focusTab = useCallback(
    (index: number) => {
      const safe = (index + TABS.length) % TABS.length;
      const node = tabRefs.current[safe];
      if (node) {
        node.focus();
        navigate(TABS[safe].path);
      }
    },
    [navigate],
  );

  const onKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLAnchorElement>, index: number) => {
      switch (evt.key) {
        case 'ArrowRight':
          evt.preventDefault();
          focusTab(index + 1);
          break;
        case 'ArrowLeft':
          evt.preventDefault();
          focusTab(index - 1);
          break;
        case 'Home':
          evt.preventDefault();
          focusTab(0);
          break;
        case 'End':
          evt.preventDefault();
          focusTab(TABS.length - 1);
          break;
        default:
          break;
      }
    },
    [focusTab],
  );

  return (
    <nav
      role="tablist"
      aria-label="Pathway Studio sections"
      className="flex items-end gap-1 bg-sidebar px-gutter-lg"
    >
      {TABS.map((tab, idx) => {
        const active = isTabActive(location.pathname, tab.path);
        const walkthroughAnchor =
          tab.id === 'studio'
            ? 'pathway-editor'
            : tab.id === 'learn'
              ? 'learning-loop'
              : undefined;
        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            end={tab.path === '/'}
            ref={(node) => {
              tabRefs.current[idx] = node;
            }}
            onKeyDown={(evt) => onKeyDown(evt, idx)}
            role="tab"
            data-tab-id={tab.id}
            data-walkthrough-anchor={walkthroughAnchor}
            // NavLink injects `aria-current="page"` automatically (router idiom).
            // The tab idiom additionally requires `aria-selected` for the
            // tablist state announcement (WAI-ARIA 1.2 + AS-6 / WCAG 2.1 AA).
            // Both attrs coexist on tabs that participate in routing.
            aria-selected={active}
            className={({ isActive }) =>
              [
                'px-gutter-md py-gutter-sm text-signal-sm font-medium transition-colors',
                'border-b-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-tab-active',
                isActive
                  ? 'border-sidebar-tab-active text-text-inverse'
                  : 'border-transparent text-sidebar-muted hover:text-text-inverse hover:border-sidebar-border',
              ].join(' ')
            }
          >
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
